import {IntlShape} from 'react-intl';
import * as Sentry from '@sentry/browser';
import DirectApi from '../../../directApi';
import {
	ExportQuickbooksInvoiceParams,
	InvoiceEntryRow,
	QuickbookProductService,
	QuickbookSetting,
	QuickbookSettingsMutationInput,
	QuickbooksSettingId,
	TaxCode,
} from './ExportInvoiceModal.type';
import Util from '../../../forecast-app/shared/util/util';
import CreateQuickbooksInvoiceMutation from '../../../mutations/CreateQuickbooksInvoiceMutation';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../../constants';
import {
	QBO_EXCLUSIVE_PAIRS,
	StoredQboExportDefaults,
	toQboExportDefaults,
} from '../../../forecast-app/admin-tab/integrations-page/export-defaults/qboExportDefaults';
import {isQboInvoicingImprovementsEnabled} from '../../../forecast-app/shared/util/FeatureUtil';

/** Each option's starting value, keyed by the window's own setting ids. */
export type QuickbookSettingDefaults = Record<QuickbooksSettingId, boolean>;

/*
 * The company's export defaults (T4-148) as the window's starting values (T4-150). An option no Admin
 * has saved starts off. A stored pair with descriptions and project name both on - possible, because two
 * single-field saves are not locked against each other (spec 013) - opens as descriptions only:
 * quickbooks-online ignores the project name while descriptions are on (postInvoice.ts:211-217), so
 * QuickBooks receives the same invoice, and the window starts inside its own rule for the pair.
 */
export const resolveQuickbookSettingDefaults = (
	company: Partial<StoredQboExportDefaults> | null | undefined
): QuickbookSettingDefaults => {
	const defaults = toQboExportDefaults(company);
	// The first field of each pair wins: QuickBooks ignores the project name while descriptions are on
	// (postInvoice.ts:211-217), so the invoice is unchanged.
	for (const [kept, dropped] of QBO_EXCLUSIVE_PAIRS) {
		if (defaults[kept] && defaults[dropped]) {
			defaults[dropped] = false;
		}
	}
	return {
		[QuickbooksSettingId.CONTROL_INVOICE_REFERENCE]: defaults.qboExportDefaultInvoiceRef,
		[QuickbooksSettingId.USE_PRODUCT_SERVICE_DESCRIPTIONS]: defaults.qboExportDefaultDescription,
		[QuickbooksSettingId.INCLUDE_PROJECT_NAME_IN_DESCRIPTION]: defaults.qboExportDefaultProjectNameInDesc,
		[QuickbooksSettingId.USE_QUICKBOOKS_TERMS]: defaults.qboExportDefaultTerms,
		[QuickbooksSettingId.USE_CUSTOMER_DEFAULT_TAX]: defaults.qboExportDefaultTax,
	};
};

/*
 * T4-151: whether a switch differs from the value the window opened with. With no opening defaults
 * (flag off, or not a QuickBooks export) nothing differs, so nothing is shown.
 */
export const isChangedFromDefault = (setting: QuickbookSetting, defaults: QuickbookSettingDefaults | null): boolean =>
	defaults !== null && setting.checked !== defaults[setting.id];

/*
 * T4-151: a label split before its last word, so the last word and the change dot can be kept together and the
 * dot never wraps onto a line of its own. `head` keeps its trailing space and is '' for a one-word label.
 */
export const splitLastWord = (label: string): [head: string, tail: string] => {
	const lastSpace = label.lastIndexOf(' ');
	return [label.slice(0, lastSpace + 1), label.slice(lastSpace + 1)];
};

// Without `defaults` every option starts off, as before T4-150.
export const getInitialQuickbookSettings = (intl: IntlShape, defaults?: QuickbookSettingDefaults): QuickbookSetting[] => {
	const {formatMessage} = intl;
	const checked = (id: QuickbooksSettingId): boolean => defaults?.[id] ?? false;

	return [
		{
			id: QuickbooksSettingId.CONTROL_INVOICE_REFERENCE,
			name: formatMessage({id: 'invoicing.quickbooks.control_reference'}),
			checked: checked(QuickbooksSettingId.CONTROL_INVOICE_REFERENCE),
			disabled: false,
		},
		{
			id: QuickbooksSettingId.USE_PRODUCT_SERVICE_DESCRIPTIONS,
			name: formatMessage({id: 'invoicing.quickbooks.use_product_service_descriptions'}),
			checked: checked(QuickbooksSettingId.USE_PRODUCT_SERVICE_DESCRIPTIONS),
			disabled: false,
		},
		{
			id: QuickbooksSettingId.INCLUDE_PROJECT_NAME_IN_DESCRIPTION,
			name: formatMessage({id: 'invoicing.quickbooks.include_project_name_in_description_when_exporting'}),
			checked: checked(QuickbooksSettingId.INCLUDE_PROJECT_NAME_IN_DESCRIPTION),
			disabled: false,
		},
		{
			id: QuickbooksSettingId.USE_QUICKBOOKS_TERMS,
			name: formatMessage({id: 'invoicing.quickbooks.use_terms'}),
			checked: checked(QuickbooksSettingId.USE_QUICKBOOKS_TERMS),
			disabled: false,
		},
		{
			id: QuickbooksSettingId.USE_CUSTOMER_DEFAULT_TAX,
			name: formatMessage({id: 'invoicing.quickbooks.use_default_tax'}),
			checked: checked(QuickbooksSettingId.USE_CUSTOMER_DEFAULT_TAX),
			disabled: false,
		},
	];
};

// The legacy modal fetched with no realm when the client carried none; the service then uses the
// company's single connection. An empty realm would request "tax_codes/" instead.
const realmPath = (realmId: string | null): string => (realmId ? `/${realmId}` : '');

const getQuickbooksTaxCodes = async (realmId: string | null): Promise<TaxCode[]> => {
	return DirectApi.Fetch(`quickbooks/tax_codes${realmPath(realmId)}`).then(json => {
		return json.map(taxCode => ({
			value: taxCode.id,
			label: taxCode.name,
			taxRate: taxCode.tax,
		}));
	});
};

const getProductsServices = async (realmId: string | null): Promise<QuickbookProductService[]> => {
	return DirectApi.Fetch(`quickbooks/items_and_services${realmPath(realmId)}`).then(json => {
		return json.map(item => ({
			value: item.id,
			label: item.name,
			description: item.description,
			unitPrice: item.unitPrice,
			taxable: item.taxable,
		}));
	});
};

/** The "No tax" option's value: no tax code is sent, so QuickBooks taxes nothing. */
export const NO_TAX_VALUE = '-1';

/*
 * Under the flag both lists are the records imported into Forecast, read through
 * graphql from service-company - no QuickBooks call. graphql resolves the organisation, including
 * the no-realm case, so the same realm path works for both. Both lists come in one response, so the
 * organisation is resolved once; a list graphql could not read comes back null. "No tax" is not a
 * stored row; today the lambda prepends it, so here the window does.
 */
type StoredQuickbooksTaxCode = {externalId: string; name: string; effectiveRate: number | string | null};
type StoredQuickbooksProduct = {
	externalId: string;
	name: string;
	description?: string | null;
	defaultUnitPrice?: number | null;
	isTaxable?: boolean | null;
};
type StoredQuickbooksExportLists = {
	taxCodes: StoredQuickbooksTaxCode[] | null;
	products: StoredQuickbooksProduct[] | null;
};

const getImportedQuickbooksExportLists = async (realmId: string | null): Promise<StoredQuickbooksExportLists> =>
	DirectApi.Fetch(`quickbooks/configuration/export-lists${realmPath(realmId)}`).then(body => {
		if (!body || typeof body !== 'object') {
			throw new Error('The QuickBooks export lists did not load');
		}
		return body;
	});

const toImportedQuickbooksTaxCodes = (rows: StoredQuickbooksTaxCode[] | null, noTaxLabel: string): TaxCode[] => {
	if (!Array.isArray(rows)) {
		throw new Error('The QuickBooks tax codes did not load');
	}
	return [
		{value: NO_TAX_VALUE, label: noTaxLabel, taxRate: 0},
		// effective_rate is DECIMAL(9,4): it can arrive as "8.0000".
		...rows.map(row => ({value: row.externalId, label: row.name, taxRate: Number(row.effectiveRate ?? 0)})),
	];
};

const toImportedProductsServices = (rows: StoredQuickbooksProduct[] | null): QuickbookProductService[] => {
	if (!Array.isArray(rows)) {
		throw new Error('The QuickBooks products and services did not load');
	}
	// Not Xero's toItemOptions: that drops items with no code, and a QuickBooks item may have no SKU.
	return rows.map(row => ({
		value: row.externalId,
		label: row.name,
		description: row.description ?? undefined,
		// Never 0 for a missing price: that would read as a real price.
		unitPrice: row.defaultUnitPrice ?? undefined,
		// US only; null for UK/CA/AU, which reads as not taxable - as the live list's absent Taxable did.
		taxable: row.isTaxable === true,
	}));
};

/** Whether any real tax code was imported; "No tax" alone does not count. */
export const hasImportedTaxCodes = (taxCodes: TaxCode[]): boolean => taxCodes.some(taxCode => taxCode.value !== NO_TAX_VALUE);

/*
 * Export needs a tax code unless the customer's default applies. "No tax" picked on purpose is a
 * choice; only an empty field is missing.
 */
export const isQuickbooksTaxCodeMissing = ({
	selected,
	customerDefaultTaxOn,
}: {
	selected: TaxCode | null;
	customerDefaultTaxOn: boolean;
}): boolean => !customerDefaultTaxOn && !selected;

/** Which of the two lists failed to load. An empty list that loaded is not a failure. */
export type QuickbooksLoadFailures = {taxCodesFailed: boolean; productsFailed: boolean};

/*
 * The two lists load independently, and `onDone` ALWAYS runs, saying which list failed. The caller
 * holds the global loader until it does, so one failed list - a rejection, or an error body that is
 * not an array - must not leave the page under an overlay only a reload clears. A failed list is
 * still set empty, but the caller is told, so it can say so rather than show an empty list as if
 * the company had nothing in it. Each failure is reported to Sentry. On success the setters run
 * together, in the same order as before.
 */
export const fetchQuickbooksData = (
	setQuickbooksTaxCodes: (taxCodes: TaxCode[]) => void,
	setSelectedQuickbooksTaxCode: (taxCode: TaxCode | null) => void,
	setProductsServices: (productsServices: QuickbookProductService[]) => void,
	onDone: (failures: QuickbooksLoadFailures) => void,
	realmId: string | null,
	// The "No tax" option's label; only the imported tax list adds it (the live one carries its own).
	noTaxLabel: string
): void => {
	// `getQuickbooksTaxCodes` is async, so a `.map` on a non-array body rejects rather than throws.
	const orNull = <T>(promise: Promise<T>, list: string): Promise<T | null> =>
		promise.catch(error => {
			Sentry.captureException(error, {tags: {quickbooksList: list}, extra: {realmId}});
			return null;
		});

	// Read once, so one load cannot mix the imported lists with the live window's default.
	const importedLists = isQboInvoicingImprovementsEnabled();
	let taxCodesLoad: Promise<TaxCode[]>;
	let productsLoad: Promise<QuickbookProductService[]>;
	if (importedLists) {
		// One request for both lists. If it fails, both lists fail; a null list fails on its own.
		const exportLists = getImportedQuickbooksExportLists(realmId);
		taxCodesLoad = exportLists.then(lists => toImportedQuickbooksTaxCodes(lists.taxCodes, noTaxLabel));
		productsLoad = exportLists.then(lists => toImportedProductsServices(lists.products));
	} else {
		taxCodesLoad = getQuickbooksTaxCodes(realmId);
		productsLoad = getProductsServices(realmId);
	}

	Promise.all([orNull(taxCodesLoad, 'tax_codes'), orNull(productsLoad, 'items_and_services')]).then(
		([taxCodes, productsServices]) => {
			setQuickbooksTaxCodes(taxCodes ?? []);
			if (taxCodes) {
				// With the imported lists the user picks a code, "No tax" included; the live window starts on "No tax".
				setSelectedQuickbooksTaxCode(
					importedLists ? null : taxCodes.find(taxCode => taxCode.value === NO_TAX_VALUE) || null
				);
			}
			setProductsServices(productsServices ?? []);

			onDone({taxCodesFailed: taxCodes === null, productsFailed: productsServices === null});
		}
	);
};

const onExportInvoiceSuccess = (result, intl: IntlShape, onSuccess: () => void): void => {
	const {formatMessage} = intl;

	onSuccess();

	const {createQuickbooksInvoice} = result || {};
	if (createQuickbooksInvoice) {
		const {success, errorMessage} = createQuickbooksInvoice;

		if (success) {
			createToast({duration: 5000, message: formatMessage({id: 'integrations.quickbooks.invoice_created'})});
		} else {
			showModal({
				type: MODAL_TYPE.WARNING,
				warningMessageId: 'invoicing.not_exported',
				warningInformation: [errorMessage || 'The invoice could not be exported.'],
				buttons: [
					{
						text: formatMessage({id: 'common.ok'}),
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.WHITE,
					},
				],
			});
		}
	}
};

const getQuickbookSettingsInput = (settings: QuickbookSetting[]): QuickbookSettingsMutationInput => {
	const settingsInput = {
		letQBOSetDescription: false,
		letQBOSetInvoiceRef: false,
		useQBOTerms: false,
		useQBODefaultTax: false,
		includeProjectNameInDesc: false,
	};

	settings.forEach(setting => {
		switch (setting.id) {
			case QuickbooksSettingId.USE_PRODUCT_SERVICE_DESCRIPTIONS:
				settingsInput.letQBOSetDescription = setting.checked;
				break;
			case QuickbooksSettingId.CONTROL_INVOICE_REFERENCE:
				settingsInput.letQBOSetInvoiceRef = setting.checked;
				break;
			case QuickbooksSettingId.USE_QUICKBOOKS_TERMS:
				settingsInput.useQBOTerms = setting.checked;
				break;
			case QuickbooksSettingId.USE_CUSTOMER_DEFAULT_TAX:
				settingsInput.useQBODefaultTax = setting.checked;
				break;
			case QuickbooksSettingId.INCLUDE_PROJECT_NAME_IN_DESCRIPTION:
				settingsInput.includeProjectNameInDesc = setting.checked;
				break;
			default:
				break;
		}
	});

	return settingsInput;
};

type QuickbooksInputParams = Pick<
	ExportQuickbooksInvoiceParams,
	'target' | 'invoice' | 'invoiceRows' | 'quickbookSettings' | 'taxCode'
>;

export const buildQuickbooksInvoiceInput = ({
	target,
	invoice,
	invoiceRows,
	quickbookSettings,
	taxCode,
}: QuickbooksInputParams) => {
	const entryMap = invoiceRows.map(row => ({entryId: row.id, itemId: row.quickbooksItem}));

	if (target.kind === 'CLIENT') {
		// Exactly what quickbooks_export_modal.js sends, so the service resolves customer and realm
		// from the client's own refs - the same request as today.
		return {
			invoiceId: invoice.id,
			forecastClientId: target.clientId,
			entryMap,
			taxCodeId: taxCode?.value ?? NO_TAX_VALUE,
			...getQuickbookSettingsInput(quickbookSettings),
		};
	}

	const {billTo} = target;
	return {
		invoiceId: invoice.id,
		billToId: billTo.id,
		quickbooksClientId: billTo?.externalId,
		// '-1' ("No tax") when no code is selected: the codes failed to load, or customer default tax is on.
		taxCodeId: taxCode?.value ?? NO_TAX_VALUE,
		entryMap,
		realmId: billTo.billFrom?.externalId,
		...getQuickbookSettingsInput(quickbookSettings),
	};
};

export const exportInvoiceToQuickbooksOnline = ({intl, onSuccess, onError, ...params}: ExportQuickbooksInvoiceParams): void => {
	Util.CommitMutation(
		CreateQuickbooksInvoiceMutation,
		buildQuickbooksInvoiceInput(params),
		result => onExportInvoiceSuccess(result, intl, onSuccess),
		true,
		null,
		onError
	);
};

/*
 * "Use QuickBooks product/service descriptions" and "Include project name in description" cannot
 * both be on - the legacy window enforces it (quickbooks_export_modal.js:77-99). Turning one on
 * turns the other off; turning one off touches nothing else.
 */
const MUTUALLY_EXCLUSIVE: Partial<Record<QuickbooksSettingId, QuickbooksSettingId>> = {
	[QuickbooksSettingId.USE_PRODUCT_SERVICE_DESCRIPTIONS]: QuickbooksSettingId.INCLUDE_PROJECT_NAME_IN_DESCRIPTION,
	[QuickbooksSettingId.INCLUDE_PROJECT_NAME_IN_DESCRIPTION]: QuickbooksSettingId.USE_PRODUCT_SERVICE_DESCRIPTIONS,
};

export const toggleQuickbookSetting = (
	settings: QuickbookSetting[],
	id: QuickbooksSettingId,
	enforceRules: boolean
): QuickbookSetting[] => {
	const toggled = settings.map(item => (item.id === id ? {...item, checked: !item.checked} : item));
	const changed = toggled.find(item => item.id === id);
	const other = changed?.checked && enforceRules ? MUTUALLY_EXCLUSIVE[changed.id] : undefined;
	return other === undefined ? toggled : toggled.map(item => (item.id === other ? {...item, checked: false} : item));
};

export const isQuickbookSettingOn = (settings: QuickbookSetting[], id: QuickbooksSettingId): boolean =>
	settings.some(setting => setting.id === id && setting.checked);

/** What QuickBooks will receive as this line's description - mirrors postInvoice.ts:170-183. */
export const getQuickbooksLineDescription = (row: InvoiceEntryRow, settings: QuickbookSetting[]): string => {
	const useQuickbooks = isQuickbookSettingOn(settings, QuickbooksSettingId.USE_PRODUCT_SERVICE_DESCRIPTIONS);
	if (useQuickbooks && row.quickbooksDescription) {
		return row.quickbooksDescription;
	}

	const description = row.description ?? '';
	const projectName = row.project?.name;
	if (
		!useQuickbooks &&
		isQuickbookSettingOn(settings, QuickbooksSettingId.INCLUDE_PROJECT_NAME_IN_DESCRIPTION) &&
		projectName &&
		!description.includes(projectName)
	) {
		return `${projectName}: ${description}`;
	}

	return description;
};
